import { LocationGroup, CreateLocationGroupRequest, UpdateLocationGroupRequest } from '../types/LocationGroup';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Service for managing location groups using the new location_groups table
 * This replaces the old dealer_groups structure
 */
export const LocationGroupService = {
  /**
   * Get all location groups for a specific organization
   */
  getGroupsByOrganization: async (organizationId: number): Promise<LocationGroup[]> => {
    try {
      const response = await fetch(`${API_URL}/location-groups?organization_id=${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch location groups:', error);
      throw error;
    }
  },

  /**
   * Get a single location group by ID
   */
  getGroupById: async (id: number): Promise<LocationGroup> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch location group with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new empty location group
   */
  createEmptyGroup: async (
    organizationId: number,
    groupName: string,
    groupDescription?: string
  ): Promise<LocationGroup> => {
    try {
      const requestData: CreateLocationGroupRequest = {
        organization_id: organizationId,
        location_ids: [], // Start with empty array
        group_name: groupName,
        group_description: groupDescription || undefined
      };

      console.log('Creating location group:', requestData);

      const response = await fetch(`${API_URL}/location-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Location group created successfully:', data.data);
      return data.data;
    } catch (error) {
      console.error('Failed to create location group:', error);
      throw error;
    }
  },

  /**
   * Add a location to an existing group
   */
  addLocationToGroup: async (groupId: number, locationId: number): Promise<LocationGroup> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/${groupId}/add-location`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify({ location_id: locationId })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to add location ${locationId} to group ${groupId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a location from an existing group
   */
  removeLocationFromGroup: async (groupId: number, locationId: number): Promise<LocationGroup> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/${groupId}/remove-location`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify({ location_id: locationId })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to remove location ${locationId} from group ${groupId}:`, error);
      throw error;
    }
  },

  /**
   * Update a location group (name, description, or location_ids)
   */
  updateGroup: async (id: number, updates: UpdateLocationGroupRequest): Promise<LocationGroup> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to update location group ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a location group
   */
  deleteGroup: async (id: number): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete location group ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get all groups that contain a specific location
   */
  getGroupsByLocation: async (organizationId: number, locationId: number): Promise<LocationGroup[]> => {
    try {
      const response = await fetch(
        `${API_URL}/location-groups?organization_id=${organizationId}&location_id=${locationId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
          },
          mode: 'cors'
        }
      );
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error(`Failed to fetch groups for location ${locationId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a location from all groups in an organization
   */
  removeLocationFromAllGroups: async (organizationId: number, locationId: number): Promise<void> => {
    try {
      const response = await fetch(
        `${API_URL}/location-groups/remove-location-from-all`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
          },
          mode: 'cors',
          body: JSON.stringify({ organization_id: organizationId, location_id: locationId })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to remove location ${locationId} from all groups:`, error);
      throw error;
    }
  },

  /**
   * Export location groups to CSV
   */
  exportGroupsCSV: async (organizationId: number): Promise<Blob> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/export/csv?organization_id=${organizationId}`, {
        method: 'GET',
        headers: {
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Failed to export groups CSV:', error);
      throw error;
    }
  },

  /**
   * Import location groups from CSV
   * @param organizationId - Organization ID
   * @param csvData - CSV data as string
   * @param mode - 'merge' (add/update) or 'replace' (delete all and recreate)
   */
  importGroupsCSV: async (
    organizationId: number, 
    csvData: string, 
    mode: 'merge' | 'replace'
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      created: number;
      updated: number;
      skipped: number;
      errors: string[];
    };
  }> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/import/csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify({
          organization_id: organizationId,
          csv_data: csvData,
          mode
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to import groups CSV:', error);
      throw error;
    }
  },

  /**
   * Download CSV template for group import
   */
  downloadTemplate: (): void => {
    const csvContent = 'Group Name,Description\n"Example Group 1","This is an example group"\n"Example Group 2","Another example"';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'location_groups_template.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  },

  /**
   * Export location assignments to CSV
   */
  exportAssignmentsCSV: async (organizationId: number): Promise<Blob> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/export/assignments/csv?organization_id=${organizationId}`, {
        method: 'GET',
        headers: {
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Failed to export assignments CSV:', error);
      throw error;
    }
  },

  /**
   * Import location assignments from CSV
   * @param organizationId - Organization ID
   * @param csvData - CSV data as string
   * @param mode - 'merge' (add to existing) or 'replace' (clear all and recreate)
   */
  importAssignmentsCSV: async (
    organizationId: number, 
    csvData: string, 
    mode: 'merge' | 'replace'
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      created: number;
      updated: number;
      skipped: number;
      errors: string[];
    };
  }> => {
    try {
      const response = await fetch(`${API_URL}/location-groups/import/assignments/csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify({
          organization_id: organizationId,
          csv_data: csvData,
          mode
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to import assignments CSV:', error);
      throw error;
    }
  },

  /**
   * Download CSV template for assignment import
   */
  downloadAssignmentTemplate: (): void => {
    const csvContent = 'Location Code,Location Name,Group Name\n"BMW001","BMW Munich Downtown","Premium Dealers"\n"BMW002","BMW Berlin Center","Premium Dealers"';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'location_assignments_template.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }
};

export default LocationGroupService;

